<template>
  <div>
    <div v-if="place">
      <page-title-area>
        <h1>{{ place.name }}</h1>
      </page-title-area>
      <event-period-selector
        :loading="loading"
        :initial-value="initialEventPeriod"
        @submit="loadEvents"
        @clear="clearEvents"
      />
      <div class="d-flex align-center mt-4">
        <locomotion-filter
          v-if="featureIsActive('s_type_crawling_pests')"
          v-model="locomotionFilter"
        />
        <v-spacer />
        <picked-event-chart-button
          :events="sortedSelectedEvents"
          :disabled="hasCrawlingInSelected"
          :locomotion-scope="chartLocomotionScope"
        />
        <span class="ml-1" />

        <template v-if="featureIsActive('s_type_scanner_upload')">
          <round-button icon="camera_alt" @click="openScannerUpload" data-testid="open_scanner_upload">
            {{ $t('scanner_upload.button') }}
          </round-button>
          <span class="ml-1" />
        </template>

        <v-tooltip top :disabled="!isMergeDisabled">
          <template v-slot:activator="{ on, attrs }">
            <div
              class="d-inline-block"
              data-testid="merge-type-tooltip-activator"
              v-on="on"
              v-bind="attrs"
            >
              <round-button
                icon="merge_type"
                :disabled="isMergeDisabled"
                @click="mergeDialogVisible = true"
                data-testid="merge-type-button"
              >
                {{ $t('merge.button') }}
              </round-button>
            </div>
          </template>
          <span>{{ mergeTooltipText }}</span>
        </v-tooltip>
        <span class="ml-1" />

        <export-excel-button
          :events="sortedSelectedEvents"
          :disabled="!featureIsActive('s_type_crawling_pests') && hasCrawlingInSelected"
        />
        <span class="ml-1" />
        <!-- トグル OFF + 徘徊種混在時も飛翔形式 CSV として出力可能（徘徊種は others 合算）なため disabled 制御は不要 -->
        <export-csv-button
          :events="sortedSelectedEvents"
          :place="place.name"
        />
      </div>
      <station-place-event-table
        ref="table"
        :events="filteredEvents"
        :loading="loading"
        @sortedSelectedEventsUpdated="updateSortedSelectedEvents"
        @editEvent="editEvent"
        @viewEvents="viewEvents"
        @eventUpdated="onEventUpdate"
      />
      <station-event-editor
        v-model="editDialogVisible"
        :place="place"
        :places="places"
        :event="currentEditingEvent"
        @update="onEventUpdate"
      />
      <station-event-detail-view
        v-model="detailViewVisible"
        :event="currentDisplayedEvent"
        :hasPrevEvent="previousEvent && !previousEvent.processing"
        :hasNextEvent="nextEvent && !nextEvent.processing"
        @previous="changeDisplayedIndexBy(-1)"
        @next="changeDisplayedIndexBy(1)"
        @pollEvent="(patch) => setEventUpdating(currentDisplayedEvent, patch)"
        @deleteEvent="showDeleteConfirmation"
        v-if="currentDisplayedEvent"
      />
      <confirm-dialog
        v-model="deleteDialogVisible"
        :message="$t('msg.delete_confirm')"
        @yes="deleteEvent"
      />

      <merge-events-dialog
        v-model="mergeDialogVisible"
        :selected-events="sortedSelectedEvents"
        @merged="onEventsMerged"
      />

      <scanner-upload-dialog
        ref="scannerUploadDialog"
        v-model="scannerUploadDialogVisible"
        :place="place"
        @uploaded="onScannerUpload"
      />
      <ai-chat-widget
        :place_id="place.id"
        :events="sortedSelectedEvents"
        :disabled="hasCrawlingInSelected"
      />
    </div>
    <page-load-spinner v-else />
  </div>
</template>

<i18n lang="yaml">
ja:
  msg.download.failed: |
   データのダウンロードに失敗しました。
   期間を変更して再度お試しください。
  msg.delete_confirm: '<strong>⚠️このデータを削除します。<span style="color: red;">もとに戻すことができませんが、本当によろしいでしょうか？</span>⚠️</strong>'
  msg.deleted: '削除しました'
  msg.delete_failed: '削除に失敗しました'
  merge:
    button: 'データ合算'
  scanner_upload:
    button: '画像アップロード'
    success: '画像解析のアップロードが完了しました（{count}件）'
  tooltip:
    crawlingNotSupported: 'この機能は床置きトラップのデータに対応していません'
    noDataSelected: 'データを選択してください'

en:
  msg.download.failed: |
    Failed to download data.
    Please try again after changing the period.
  msg.delete_confirm: >-
    <strong>⚠️ This event will be deleted
    <span style="color: red;">and cannot be restored. Are you sure you want to proceed?</span>
    ⚠️</strong>
  msg.deleted: 'Deleted'
  msg.delete_failed: 'Failed to delete'
  merge:
    button: 'Merge Data'
  scanner_upload:
    button: 'Image Upload'
    success: 'Image analysis upload completed ({count} files)'
  tooltip:
    crawlingNotSupported: 'This feature does not support crawling pest data'
    noDataSelected: 'Please select data'
</i18n>

<script>
import { subMonths } from 'date-fns';

import dateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import { locomotionOf, LOCOMOTION_SCOPE, LOCOMOTION_CRAWLING } from '@/libs/insectTypes';
import Notifications from '@/mixins/notifications';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import PageLoadSpinner from '@/components/atoms/PageLoadSpinner';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import RoundButton from '@/components/atoms/RoundButton';
import StationEventEditor from '@/components/organisms/StationEventEditor';
import AiChatWidget from './AiChatWidget';
import EventPeriodSelector from './EventPeriodSelector';
import ExportCsvButton from './ExportCsvButton';
import ExportExcelButton from './ExportExcelButton';
import LocomotionFilter from './LocomotionFilter';
import MergeEventsDialog from './MergeEventsDialog';
import PickedEventChartButton from './PickedEventChartButton';
import ScannerUploadDialog from './ScannerUploadDialog';
import StationEventDetailView from './StationEventDetailView';
import StationPlaceEventTable from './StationPlaceEventTable';

export default {
  name: 'station-place-events',
  components: {
    AiChatWidget,
    ConfirmDialog,
    EventPeriodSelector,
    ExportCsvButton,
    ExportExcelButton,
    LocomotionFilter,
    MergeEventsDialog,
    PageLoadSpinner,
    PageTitleArea,
    PickedEventChartButton,
    RoundButton,
    ScannerUploadDialog,
    StationEventDetailView,
    StationEventEditor,
    StationPlaceEventTable,
  },
  mixins: [
    Features,
    Notifications,
    dateTimeFormatting,
  ],
  props: {
    placeId: {
      type: String,
    },
  },
  data() {
    return {
      place: null,
      places: [],
      loading: true,
      events: [],
      locomotionFilter: 'all',
      sortedSelectedEvents: [],
      // Edit data
      editDialogVisible: false,
      currentEditingEvent: null,
      // View data
      detailViewVisible: false,
      displayedEvents: [],
      displayedIndex: null,
      // Scanner upload
      scannerUploadDialogVisible: false,
      // Delete confirmation
      deleteDialogVisible: false,
      // Merge dialog
      mergeDialogVisible: false,
    };
  },
  computed: {
    // `calculateLocomotionScope` (`@/libs/insectTypes`) と同じトグル + 徘徊判定。
    // signature 差で流用できないが、events 配列対応の共通ヘルパーが追加されたら統合する。
    chartLocomotionScope() {
      if (!this.featureIsActive('s_type_crawling_pests')) {
        return LOCOMOTION_SCOPE.FLYING_ONLY;
      }
      const hasCrawling = this.sortedSelectedEvents
        .some(event => locomotionOf(event) === LOCOMOTION_CRAWLING);
      return hasCrawling ? LOCOMOTION_SCOPE.ALL : LOCOMOTION_SCOPE.FLYING_ONLY;
    },
    // 出力機能の利用制限は s_type_crawling_pests トグル OFF 時の過去データにも適用される（Decision 0038）
    // chartLocomotionScope はトグル OFF 時に early return するのに対し、こちらはトグル無関係に判定する
    hasCrawlingInSelected() {
      return this.sortedSelectedEvents
        .some(event => locomotionOf(event) === LOCOMOTION_CRAWLING);
    },
    isMergeDisabled() {
      return this.hasCrawlingInSelected || this.sortedSelectedEvents.length === 0;
    },
    // 暫定: 徘徊性データは段階出荷中のため、データ合算は徘徊性混在時に disabled としている。
    // Redmine #111556 (S床データの合算対応) 完了時に hasCrawlingInSelected 分岐を削除する。
    mergeTooltipText() {
      if (this.hasCrawlingInSelected) return this.$t('tooltip.crawlingNotSupported');
      if (this.sortedSelectedEvents.length === 0) return this.$t('tooltip.noDataSelected');
      return '';
    },
    currentDisplayedEvent() {
      return this.displayedEvents[this.displayedIndex];
    },
    filteredEvents() {
      if (this.locomotionFilter === 'all') {
        return this.events;
      }
      return this.events.filter(event => locomotionOf(event) === this.locomotionFilter);
    },
    initialEventPeriod() {
      const halfYearsAgo = this.formatMonthForApi(subMonths(new Date(), 6));
      const thisMonth = this.formatMonthForApi(new Date());
      return { from: halfYearsAgo, to: thisMonth };
    },
    nextEvent() {
      return this.currentDisplayedEvent && this.displayedEvents[this.displayedIndex + 1];
    },
    previousEvent() {
      return this.currentDisplayedEvent && this.displayedEvents[this.displayedIndex - 1];
    },
  },
  inject: ['api'],
  watch: {
    locomotionFilter() {
      // 非表示になる行の選択を残さないため、フィルタ変更時に選択を解除する
      this.$refs.table.clearSelection();
    },
  },
  mounted: async function () {
    this.place = await this.api.fetchStationPlace(this.placeId);
    await this.loadStationPlaces();
    await this.loadEvents(this.initialEventPeriod);
  },
  methods: {
    changeDisplayedIndexBy(value) {
      this.displayedIndex = this._.clamp(
        this.displayedIndex + value,
        0,
        this.displayedEvents.length - 1,
      );
    },
    clearEvents() {
      this.events = [];
    },
    editEvent(event) {
      this.currentEditingEvent = event;
      this.editDialogVisible = true;
    },
    loadEvents: async function (period) {
      this.loading = true;
      try {
        this.events = await this.api.fetchStationPlaceEvents(this.placeId, period);
      } catch (error) {
        this.notifyError('msg.download.failed');
      } finally {
        this.loading = false;
      }
    },
    async loadStationPlaces() {
      try {
        this.places = await this.api.fetchStationPlaces();
        this.place = this.places.find(p => p.id === this.placeId);
      } catch (error) {
        this.notifyError('msg.download.failed');
      }
    },
    onEventsMerged({ mergedEvent, deletedEventId }) {
      this.events = this.events
        .filter(e => e.id !== deletedEventId)
        .map(e => (e.id === mergedEvent.id ? mergedEvent : e));
      this.loadStationPlaces();
    },
    onEventUpdate(event) {
      this.events = this.updateEventList(this.events, event);
      if (this.displayedEvents) {
        this.displayedEvents = this.updateEventList(this.displayedEvents, event);
      }
      this.loadStationPlaces();
    },
    setEventUpdating(event, patch = {}) {
      const merged = { ...event, updating: true, ...patch };
      this.onEventUpdate(merged);
    },
    updateEventList(events, updatedEvent) {
      const result = [];
      events.forEach((ev) => {
        if (ev.id === updatedEvent.id) {
          if (updatedEvent.place_id === this.placeId) {
            result.push(updatedEvent);
          }
        } else {
          result.push(ev);
        }
      });
      return result;
    },
    updateSortedSelectedEvents(value) {
      this.sortedSelectedEvents = value;
    },
    viewEvents({ events, index }) {
      this.displayedEvents = events;
      this.displayedIndex = index;
      this.detailViewVisible = true;
    },

    openScannerUpload() {
      this.scannerUploadDialogVisible = true;
    },
    onScannerUpload({ files, event }) {
      this.notifySuccess('scanner_upload.success', { count: files });
      // 新しく作成されたイベントをリストの先頭に追加
      this.events.unshift(event);

      // 即座にダイアログを閉じる
      this.$refs.scannerUploadDialog.close();

      // トラップNoの選択肢を更新するためplacesを再取得
      this.loadStationPlaces();
    },
    showDeleteConfirmation() {
      this.deleteDialogVisible = true;
    },
    async deleteEvent() {
      const eventToDelete = this.currentDisplayedEvent;
      try {
        await this.api.deleteStationPlaceEvent(eventToDelete.id);

        this.events = this.events.filter(e => e.id !== eventToDelete.id);
        this.displayedEvents = this.displayedEvents.filter(e => e.id !== eventToDelete.id);

        if (this.displayedEvents.length === 0) {
          this.detailViewVisible = false;
        } else if (this.displayedIndex >= this.displayedEvents.length) {
          this.displayedIndex = this.displayedEvents.length - 1;
        }

        this.notifySuccess('msg.deleted');
      } catch (error) {
        this.notifyError('msg.delete_failed');
      } finally {
        this.deleteDialogVisible = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
